import { Page } from "../enums/Page";
import { css, htmlArray } from "../helpers/webComponents";

// tsdoc toevoegen

/** CSS affecting the {@link EasterEggComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize", sans-serif;
        width: 100%;
        max-width: 1024px;
        height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
        background: url("assets/img/rooms/backgroundcomponent.png") no-repeat center center;
        background-size: cover;
    }

    .book {
        position: relative;
        width: 100%;
        max-width: 570px;
        height: auto;
        min-height: 450px;
        background: #E0D3AF;
        opacity: 0.9;
        border: 10px solid #9B111E;
        box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.3);
        font-family: "Onesize", sans-serif;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 20px;
        overflow: auto;
    }

    h2 {
        font-family: "Onesize", sans-serif;
        text-align: center;
        font-size: 24px;
        color: #4B2E39;
        margin-bottom: 15px;
        text-transform: uppercase;
    }

    ul {
        list-style-type: none;
        padding: 0;
        margin: 0;
        width: 100%;
        text-align: left;
        overflow: auto;
    }

    li {
        font-family: "Onesize", sans-serif;
        background: #4e4e4e;
        color: #ffd700;
        padding: 10px;
        margin: 5px 0;
        border-radius: 5px;
        font-size: 18px;
        border: 1px solid #6d6d6d;
    }

    .empty-message {
        font-style: italic;
        color: #bebbbb;
    }

    .return-button {
        font-family: "Onesize", sans-serif;
        background-color: #780606;
        border: 1px solid #6d6d6d;
        padding: 10px 20px;
        margin-top: 15px;
        text-transform: uppercase;
        cursor: pointer;
        display: block;
        width: 100%;
        text-align: center;
        color: white;
        border-radius: 5px;
    }

    .return-button:hover {
        background-color: #bebbbb;
        color: black;
    }
`;

/**
 * Een custom webcomponent dat de 'Easter Egg' functionaliteit beheert.
 * Dit component toont een lijst met spelersnamen en biedt een optie om terug te keren naar de bibliotheek.
 */
export class EasterEggComponent extends HTMLElement {
    // private _bookofthePlayersModel: BookofthePlayersModel;
    /**
     * Constructor voor de {@link EasterEggComponent} klasse.
     * Initialiseert het webcomponent en voegt event listeners toe.
     */
    public constructor() {
        super();
        // this._bookofthePlayersModel = new BookofthePlayersModel();
        this.attachShadow({ mode: "open" });
        this.setupEventListeners();
    }

    /**
     * Voegt event listeners toe aan het component.
     */
    private setupEventListeners(): void {
        this.addEventListener("DOMContentLoaded", () => this.fetchNames());
    }

    /**
     * Wordt aangeroepen wanneer het element aan de DOM wordt toegevoegd.
     * Roept de `fetchNames` methode aan om spelersnamen op te halen.
     */
    public async connectedCallback(): Promise<void> {
        await this.fetchNames();
    }

    private async fetchNames(): Promise<void> {
        try {
            const response: Response = await fetch("http://localhost:3001/gloria/checkNames"); // Roep backend API aan
            if (!response.ok) {
                throw new Error("Failed to fetch names");
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const names: string[] = await response.json();
            this.render(names);
        }
        catch (error) {
            console.error("Error fetching names:", error);
            this.render([]);
        }
    }

    /**
     * Genereert een HTML-lijst van unieke spelersnamen.
     * @param names - Een array van spelersnamen.
     * @returns Een HTML-string met `<li>` elementen.
     */
    private generateNameList(names: string[]): string {
        const uniqueNames: string[] = Array.from(new Set(names)); // Verwijder duplicaten
        return uniqueNames.length > 0
            ? uniqueNames.map(name => `<li>${name}</li>`).join("")
            : "<li class=\"empty-message\">No players found</li>";
    }

    /**
     * Rendert het component met de opgehaalde spelersnamen.
     * @param names - Een array van spelersnamen.
     */
    private render(names: string[]): void {
        if (!this.shadowRoot) return;

        const elements: HTMLElement[] = htmlArray`
            <style>${styles}</style>
            <div class="book">
                <h2>Book of the Players</h2>
                <ul>
                    ${this.generateNameList(names)}
                </ul>
                <button class="return-button">Return to Library</button>
            </div>
        `;

        this.shadowRoot.innerHTML = "";
        this.shadowRoot.append(...elements);

        // Event listener voor return button
        const returnButton: Element | null = this.shadowRoot.querySelector(".return-button");
        returnButton?.addEventListener("click", () => this.returnToLibrary());
    }

    /**
     * Stuurt een custom event om terug te keren naar de bibliotheek.
     */
    private returnToLibrary(): void {
        const event: CustomEvent = new CustomEvent("game-switch-page", {
            detail: { page: Page.Canvas }, // Page.Canvas verwijst naar de game-canvas
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }

    /**
     * Registreert het custom element zodat het in HTML gebruikt kan worden.
     */
    public static register(): void {
        if (!customElements.get("easter-egg-component")) {
            customElements.define("easter-egg-component", EasterEggComponent);
        }
    }
}
